"use client"

import React, { useEffect, useState } from 'react'
import styles from './styles.module.scss'
import { useTranslations } from 'next-intl'
import SvgArrow from '@/src/assets/svgs/collection/arrow-left.svg'
import { useShoppingCart } from '@/src/context/shopping-cart/shopping-cart-context'
import { useMessage } from '@/src/context/message/message-context'
import ActionInput from '@/src/components/misc/action-elements/action-input/action-input'
import { CouponService } from '@/src/services/benefits/coupon-service'
import { useMerchant } from '@/src/context/merchant/merchant-context'
import useCoupon from '@/src/hooks/shopping-cart/coupon'
import RemoveCoupon from '../remove-coupon/remove-coupon'
import RemoveCashback from '../remove-cashback/remove-cashback'
import { usePathname } from 'next/navigation'
import { useAppAccessToken } from '@/src/context/app-access-token/app-access-token-context'

export default function ApplyCoupon() {
    const t = useTranslations('shopping-cart')
    const { shoppingCart, addCoupon, removeCoupon, removeCashback, cashbackDiscountPrice } = useShoppingCart()
    const { showMessage } = useMessage()
    const { merchantSummary } = useMerchant()
    const { validate } = useCoupon()
    const pathname = usePathname()
    const { appAccessToken } = useAppAccessToken()

    const [showInput, setShowInput] = useState(false)
    const [name, setName] = useState<string | undefined>()
    const [isLoading, setIsLoading] = useState<boolean>(false)

    const applyCoupon = async () => {
        if (!name || (name && name.trim().length < 1))
            return showMessage('Insira o código do cupom.')

        try {
            setIsLoading(true)

            const response = await CouponService.getByName(encodeURIComponent(name), merchantSummary?.id!, appAccessToken)

            if (response.status === 404)
                return showMessage(`Cupom ${name} inválido.`)

            if (!response.success || !response.model)
                throw Error(response.errorMessage)

            const validation = validate(response.model)

            if (!validation.isValid)
                return showMessage(validation.message)

            addCoupon(response.model)
        }
        catch (e: any) {
            const error = e as Error
            showMessage(error.message, { type: 'danger', error: error })
        }
        finally {
            setIsLoading(false)
        }
    }

    const removeCurrentCashback = () => {
        removeCashback()

        if (pathname.startsWith('/fidelidade'))
            window.location.reload()
    }

    useEffect(() => {
        setShowInput(!!shoppingCart?.coupon)
    }, [shoppingCart])

    return (
        <div className={styles.container}>
            <div className={styles.subContainer}>
                {
                    cashbackDiscountPrice <= 0 &&
                    <>
                        <div onClick={() => setShowInput(!showInput)} className={styles.collapse}>
                            <h5 className={`${styles.title} font-md`}>{t('coupon')}</h5>
                            <SvgArrow className={showInput ? styles.hideRotateArrow : styles.showRotateArrow} />
                        </div>
                        {
                            showInput &&
                            <div className={styles.applyCouponArea}>
                                {
                                    (shoppingCart?.coupon && shoppingCart)
                                        ?
                                        <RemoveCoupon name={shoppingCart.coupon.name} remove={removeCoupon} />
                                        :
                                        <ActionInput
                                            sideElement='button'
                                            classNameButton={styles.applyButton}
                                            titleButton='Aplicar'
                                            placeholder={t('couponCode')}
                                            classNameContainer={styles.inputArea}
                                            onButtonClick={applyCoupon}
                                            onChange={(e) => setName(e.target.value)}
                                            defaultValue={name?.toLocaleUpperCase()}
                                            onKeyDown={e => e.key === 'Enter' && applyCoupon()}
                                            buttonSize='small'
                                            isButtonLoading={isLoading}
                                        />
                                }
                            </div>
                        }
                    </>
                }
                {cashbackDiscountPrice > 0 && <RemoveCashback remove={removeCurrentCashback} />}
            </div>
        </div>
    )
}
